import React, { useEffect, useRef } from "react";
import L from "leaflet";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";

// Custom div icon
const makeIcon = (color, label) => L.divIcon({
  className: "",
  html: `<div class="tour-marker ${color}">${label || ""}</div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

const escape = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
}[c]));

export default function MapView({ data, height = "70vh", onMarkerClick }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const { lang, tx } = useI18n();

  useEffect(() => {
    if (!containerRef.current || !data) return;

    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current, {
        center: [data.center.lat, data.center.lng],
        zoom: data.zoom || 13,
        scrollWheelZoom: true,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "© OpenStreetMap",
      }).addTo(mapRef.current);
    } else {
      mapRef.current.setView([data.center.lat, data.center.lng], data.zoom || 13);
    }

    // clear existing markers
    mapRef.current.eachLayer((layer) => {
      if (layer instanceof L.Marker) mapRef.current.removeLayer(layer);
    });

    const markers = [];
    (data.markers || []).forEach((m) => {
      const cat = m.category || {};
      const color = cat.color === "green" ? "green" : "red";
      const initial = (tx(cat.name) || "•").trim().charAt(0).toUpperCase();
      const icon = makeIcon(color, initial);
      const marker = L.marker([m.latitude, m.longitude], { icon });
      const title = escape(tx(m.title));
      const desc = escape(tx(m.short_description));
      const img = m.image_url ? `<img src="${escape(m.image_url)}" alt="" style="width:100%;height:80px;object-fit:cover;border-radius:8px;margin-bottom:6px" />` : "";
      const html = `
        <div style="min-width:200px;max-width:240px">
          ${img}
          <div style="font-family:'Outfit',sans-serif;font-weight:600;font-size:14px;line-height:1.2;margin-bottom:4px">${title}</div>
          <div style="font-size:12px;color:#555;margin-bottom:6px">${desc}</div>
          <a href="/places/${escape(m.slug)}" style="font-size:12px;color:hsl(142 71% 38%);font-weight:600;text-decoration:none">${lang === "es" ? "Ver detalles →" : lang === "fr" ? "Voir détails →" : "View details →"}</a>
        </div>`;
      marker.bindPopup(html);
      if (onMarkerClick) marker.on("click", () => onMarkerClick(m));
      marker.addTo(mapRef.current);
      markers.push(marker);
    });
  }, [data, lang, tx, onMarkerClick]);

  // cleanup
  useEffect(() => () => {
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  }, []);

  return <div ref={containerRef} style={{ width: "100%", height }} className="rounded-2xl border border-border overflow-hidden" data-testid="leaflet-map" />;
}
